import { Box, Button, Card, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { bloqcibeApi } from "../../store/slices/apiSlice";
import { useSelector } from "react-redux";
import React, { useEffect, useMemo, useState } from "react";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { findDoaTrialMatch } from "./doaRouteUtils";

const TrialInfo = (props) => {
    const { setAllTasks, trialId, siteId, onTrialContextChange } = props;
    const sponsorId = useSelector((state) => state.auth.sponsorId);
    const [protocolInfo, setProtocolInfo] = useState({});
    const [trialInfo, setTrialInfo] = useState([]);
    const [sponsorData , setSponsorData] = useState({});
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const [fetchDAO] =
        bloqcibeApi.endpoints.fetchDAO.useLazyQuery();
    const [fetchAllDAOTasks, { data: allDOATasks }] =
        bloqcibeApi.endpoints.fetchAllDAOTasks.useLazyQuery();
    useEffect(() => {
        if (!sponsorId) return;
        (async () => {
            const result = await fetchDAO({
                sponsorId: sponsorId,
            });
            if (!result?.data?.trials) return;
            const trialsData = result.data.trials.filter(
                (trial) => trial?.trial?.status === 'Ongoing'
            );
            setTrialInfo(trialsData);
            setSponsorData(result?.data?.sponsor ?? {});
        })();
    }, [fetchDAO, sponsorId]);

    const selectedTrial = useMemo(() => {
        if (!trialInfo.length) return null;
        const match = findDoaTrialMatch(trialInfo, trialId, siteId);
        return match ?? trialInfo[0];
    }, [trialInfo, trialId, siteId]);

    useEffect(() => {
        if (!trialInfo.length || !onTrialContextChange) return;

        const match = findDoaTrialMatch(trialInfo, trialId, siteId);
        if (!match) {
            const first = trialInfo[0];
            onTrialContextChange(
                {
                    trialId: first.trial.id,
                    siteId: first.site.id,
                    siteTrialId: first.site.trialSiteId,
                },
                { replace: true }
            );
        }
    }, [trialInfo, trialId, siteId, onTrialContextChange]);

    useEffect(() => {
        if (!selectedTrial) return;

        const trialData = selectedTrial.trial;
        const siteData = selectedTrial.site;
        const userData = selectedTrial.users;
        setProtocolInfo({
            sponsor: sponsorData?.orgnizationName,
            protocolNo: trialData?.protocolNumber,
            trialTitle: trialData?.trialTitle,
            site: siteData?.orgname,
            PI: userData.length > 0 ? `${userData[0]?.firstName} ${userData[0]?.lastName}` : ' ---- ----',
        });
        (async () => {
            await fetchAllDAOTasks({
                trialId: trialData.id,
                siteId: siteData.id,
            });
        })();
    }, [fetchAllDAOTasks, selectedTrial, sponsorData?.orgnizationName]);

    useEffect(() => {
        setAllTasks(allDOATasks);
    }, [allDOATasks, setAllTasks])
    
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClickTrial = (trial) => {
        onTrialContextChange?.({
            trialId: trial.trial.id,
            siteId: trial.site.id,
            siteTrialId: trial.site.trialSiteId,
        });
        handleClose();
    };

    const hasMultipleTrials = trialInfo.length > 1;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', columnGap: 3}}>
            <Card sx={{ padding: 3, width: '24%', borderRadius: 4 }}>
                <Typography sx={{ fontSize: 16, color: 'rgb(130, 130, 130)', fontWeight: 500 }}>
                    Sponsor
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {protocolInfo?.sponsor}
                </Typography>
            </Card>
            <Card sx={{ padding: 3, width: '24%', borderRadius: 4 }}>
                
                <Box sx={{ display: 'flex', columnGap: 2, alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 16, color: 'rgb(130, 130, 130)', fontWeight: 500 }}>
                        Protocol No
                    </Typography>
                    <Tooltip title={
                        <Box sx={{ display:'flex', flexDirection:'column', rowGap: 0.5, padding:1 }}>
                            <Typography variant="subtitle2">Name of Study:</Typography>
                            <Typography>{protocolInfo?.trialTitle}</Typography>
                        </Box>
                    }><InfoOutlinedIcon color="primary" /></Tooltip>
                </Box>
                <Box sx={{ display: 'flex' }}>
                    <Button
                        onClick={hasMultipleTrials ? handleClick : undefined}
                        endIcon={
                            hasMultipleTrials ? (
                                <KeyboardArrowDownIcon sx={{ color: 'black' }} />
                            ) : null
                        }
                        size="small"
                        fullWidth
                        disableRipple
                        disabled={!hasMultipleTrials}
                        sx={{
                            marginLeft: 0,
                            justifyContent: 'space-between',
                            padding: '5px 0px',
                            borderBottom: '2px solid transparent',
                            borderRadius: 0,
                            '&:hover': {
                                backgroundColor: 'transparent',
                                borderBottom: hasMultipleTrials ? '2px solid' : '2px solid transparent',
                            },
                            '&.Mui-disabled': {
                                color: 'inherit',
                            },
                        }}
                        aria-controls={open ? 'protocol-menu' : undefined}
                        aria-haspopup={hasMultipleTrials ? 'true' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                    >
                        <Tooltip title={protocolInfo?.protocolNo || ''} disableHoverListener={!protocolInfo?.protocolNo}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'black',
                                    fontWeight: 600,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                }}
                            >
                                {protocolInfo?.protocolNo}
                            </Typography>
                        </Tooltip>
                    </Button>
                </Box>
                <Menu
                    id="protocol-menu"
                    anchorEl={anchorEl}
                    open={open && hasMultipleTrials}
                    onClose={handleClose}
                    MenuListProps={{ 'aria-labelledby': 'protocol-menu-button' }}
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                            minWidth: anchorEl?.clientWidth,
                        },
                    }}
                    transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                >
                    {trialInfo.map((trial) => (
                        <MenuItem
                            key={`${trial.trial?.id}-${trial.site?.id}`}
                            onClick={() => handleClickTrial(trial)}
                            selected={selectedTrial?.trial?.id === trial.trial?.id && selectedTrial?.site?.id === trial.site?.id}
                        >
                            {trial.trial?.protocolNumber}
                        </MenuItem>
                    ))}
                </Menu>
            </Card>
            <Card sx={{ padding: 3, width: '24%', borderRadius: 4 }}>
                <Typography sx={{ fontSize: 16, color: 'rgb(130, 130, 130)', fontWeight: 500 }}>
                    Site
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {protocolInfo?.site}
                </Typography>
            </Card>
            <Card sx={{ padding: 3, width: '24%', borderRadius: 4 }}>
                <Typography sx={{ fontSize: 16, color: 'rgb(130, 130, 130)', fontWeight: 500 }}>
                    Principle Investigator
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {protocolInfo?.PI}
                </Typography>
            </Card>
        </Box>
    )
}

export default TrialInfo;